// src/pages/BulkListingGenerator/components/BulkFormHelper.js
import React from 'react';
import formStyles from '../../SingleListingGenerator/SingleListingGeneratorPage.module.css';
import styles from './BulkFormHelper.module.css';

import { SingleListingFormProvider } from '../../../context/SingleListingFormContext';
import StepNavigationTabs from '../../SingleListingGenerator/components/StepNavigationTabs';
import FormSteps from '../../SingleListingGenerator/components/FormSteps';
import { initialExistingInput } from '../../SingleListingGenerator/constants';

import { useBulkFormState } from './useBulkFormState';
import SkuSidebar from './SkuSidebar';
import FloatingSkuDropdown from './FloatingSkuDropdown';
import LoadingOverlay from '../../../components/UI/LoadingOverlay';
import QueueNotificationPopup from '../../../components/UI/QueueNotificationPopup';
import {
    findColumnValue,
    BULLET_1_COLUMNS, BULLET_2_COLUMNS, BULLET_3_COLUMNS, BULLET_4_COLUMNS, BULLET_5_COLUMNS,
    CORE_DETAIL_1_COLUMNS, CORE_DETAIL_2_COLUMNS, CORE_DETAIL_3_COLUMNS, CORE_DETAIL_4_COLUMNS, CORE_DETAIL_5_COLUMNS,
    CORE_DETAIL_ONLY_1_COLUMNS, CORE_DETAIL_ONLY_2_COLUMNS, CORE_DETAIL_ONLY_3_COLUMNS, CORE_DETAIL_ONLY_4_COLUMNS, CORE_DETAIL_ONLY_5_COLUMNS,
    TITLE_COLUMNS, DESCRIPTION_COLUMNS, BANNED_KEYWORDS_COLUMNS,
    APPLICATION_1_COLUMNS, APPLICATION_2_COLUMNS,
    PARENT_CHILD_ATTR_1_COLUMNS, PARENT_CHILD_ATTR_1_VALUE_COLUMNS,
    PARENT_CHILD_ATTR_2_COLUMNS, PARENT_CHILD_ATTR_2_VALUE_COLUMNS,
    PARENT_CHILD_ATTR_3_COLUMNS, PARENT_CHILD_ATTR_3_VALUE_COLUMNS,
    COLOR_COLUMNS, SIZE_COLUMNS,
} from './bulkColumnMappings';

// ── Existing-mode config ────────────────────────────────────────────────────
const existingModeConfig = {
    listingMode: 'existing',
    initialFormStep: 2,
    featureColumns: {
        // In existing mode, keep "Existing Listing" bullets separate from "Key Features".
        // Key Features should not auto-fill from bullet points.
        feature1: CORE_DETAIL_ONLY_1_COLUMNS,
        feature2: CORE_DETAIL_ONLY_2_COLUMNS,
        feature3: CORE_DETAIL_ONLY_3_COLUMNS,
        feature4: CORE_DETAIL_ONLY_4_COLUMNS,
        feature5: CORE_DETAIL_ONLY_5_COLUMNS,
    },
    extraFormFields: () => ({}),
    extractExistingInput: (row) => ({
        asin: findColumnValue(row, ['SKU ID', 'ASIN', 'asin']) || '',
        title: findColumnValue(row, TITLE_COLUMNS),
        description: findColumnValue(row, DESCRIPTION_COLUMNS),
        bullets: [
            findColumnValue(row, BULLET_1_COLUMNS),
            findColumnValue(row, BULLET_2_COLUMNS),
            findColumnValue(row, BULLET_3_COLUMNS),
            findColumnValue(row, BULLET_4_COLUMNS),
            findColumnValue(row, BULLET_5_COLUMNS),
        ]
    }),
    extractExtraSkuFields: (row) => ({
        parentChildAttributes: [
            { name: 'Color', value: findColumnValue(row, COLOR_COLUMNS) },
            { name: 'Size', value: findColumnValue(row, SIZE_COLUMNS) },
        ].filter(attr => attr.value)
    }),
    isFormStepValid: (step, { existingInput, formData, showVariantsTab, isVariantsValid, bPlusSequence, aPlusSequence }) => {
        switch (step) {
            case 1: return true;
            case 2: return !!(existingInput.title?.trim());
            case 3: return !!(formData.brandName?.trim() && formData.modelNumber?.trim() && formData.skuId?.trim());
            case 4: return !!(formData.briefDescription?.trim() && formData.feature1?.trim());
            case 5: return showVariantsTab ? isVariantsValid : !!(bPlusSequence?.length > 0 && aPlusSequence?.length > 0);
            case 6: return !!(bPlusSequence?.length > 0 && aPlusSequence?.length > 0);
            default: return false;
        }
    },
    getFormStepValidation: (step, { existingInput, formData, showVariantsTab, isVariantsValid, bPlusSequence, aPlusSequence }) => {
        const invalid = new Set();
        let valid = false;
        switch (step) {
            case 1: valid = true; break;
            case 2:
                valid = !!(existingInput.title?.trim());
                if (!existingInput.title?.trim()) invalid.add('title');
                break;
            case 3:
                valid = !!(formData.brandName?.trim() && formData.modelNumber?.trim() && formData.skuId?.trim());
                if (!formData.brandName?.trim()) invalid.add('brandName');
                if (!formData.modelNumber?.trim()) invalid.add('modelNumber');
                if (!formData.skuId?.trim()) invalid.add('skuId');
                break;
            case 4:
                valid = !!(formData.briefDescription?.trim() && formData.feature1?.trim());
                if (!formData.briefDescription?.trim()) invalid.add('briefDescription');
                if (!formData.feature1?.trim()) invalid.add('feature1');
                break;
            case 5: valid = showVariantsTab ? isVariantsValid : !!(bPlusSequence?.length > 0 && aPlusSequence?.length > 0); break;
            case 6: valid = !!(bPlusSequence?.length > 0 && aPlusSequence?.length > 0); break;
            default: break;
        }
        return { valid, invalidFields: invalid };
    },
    buildSkuEntryExtra: (skuItem) => ({
        parentChildAttributes: skuItem?.parentChildAttributes || []
    }),
};

// ── New-mode config ─────────────────────────────────────────────────────────
const newModeConfig = {
    listingMode: 'new',
    initialFormStep: 2, // Open Details tab by default (after Excel+ZIP parse)
    featureColumns: {
        feature1: CORE_DETAIL_1_COLUMNS,
        feature2: CORE_DETAIL_2_COLUMNS,
        feature3: CORE_DETAIL_3_COLUMNS,
        feature4: CORE_DETAIL_4_COLUMNS,
        feature5: CORE_DETAIL_5_COLUMNS,
    },
    extraFormFields: (row) => ({
        bannedKeywords: findColumnValue(row, BANNED_KEYWORDS_COLUMNS),
        application1: findColumnValue(row, APPLICATION_1_COLUMNS),
        application2: findColumnValue(row, APPLICATION_2_COLUMNS),
    }),
    extractExistingInput: () => ({ ...initialExistingInput }),
    extractExtraSkuFields: (row) => ({
        parentChildAttributes: [
            { name: findColumnValue(row, PARENT_CHILD_ATTR_1_COLUMNS), value: findColumnValue(row, PARENT_CHILD_ATTR_1_VALUE_COLUMNS) },
            { name: findColumnValue(row, PARENT_CHILD_ATTR_2_COLUMNS), value: findColumnValue(row, PARENT_CHILD_ATTR_2_VALUE_COLUMNS) },
            { name: findColumnValue(row, PARENT_CHILD_ATTR_3_COLUMNS), value: findColumnValue(row, PARENT_CHILD_ATTR_3_VALUE_COLUMNS) },
        ].filter(attr => attr.name || attr.value)
    }),
    // Step numbers must match actual formStep values (initialFormStep: 2)
    // Step 2 = Details, Step 3 = Key Features, Step 4/5 = Infographics
    isFormStepValid: (step, { formData, showVariantsTab, isVariantsValid, bPlusSequence, aPlusSequence }) => {
        switch (step) {
            case 1: return true; // Product Identity – skipped in bulk
            case 2: return !!(formData.brandName?.trim() && formData.productType?.trim() && formData.modelNumber?.trim() && formData.skuId?.trim());
            case 3: return !!(formData.briefDescription?.trim() && formData.feature1?.trim());
            case 4: return showVariantsTab ? isVariantsValid : !!(bPlusSequence?.length > 0 && aPlusSequence?.length > 0);
            case 5: return !!(bPlusSequence?.length > 0 && aPlusSequence?.length > 0);
            case 6: return !!(bPlusSequence?.length > 0 && aPlusSequence?.length > 0);
            default: return false;
        }
    },
    getFormStepValidation: (step, { formData, showVariantsTab, isVariantsValid, bPlusSequence, aPlusSequence }) => {
        const invalid = new Set();
        let valid = false;
        switch (step) {
            case 1: valid = true; break;
            case 2:
                valid = !!(formData.brandName?.trim() && formData.productType?.trim() && formData.modelNumber?.trim() && formData.skuId?.trim());
                if (!formData.brandName?.trim()) invalid.add('brandName');
                if (!formData.productType?.trim()) invalid.add('productType');
                if (!formData.modelNumber?.trim()) invalid.add('modelNumber');
                if (!formData.skuId?.trim()) invalid.add('skuId');
                break;
            case 3:
                valid = !!(formData.briefDescription?.trim() && formData.feature1?.trim());
                if (!formData.briefDescription?.trim()) invalid.add('briefDescription');
                if (!formData.feature1?.trim()) invalid.add('feature1');
                break;
            case 4: valid = showVariantsTab ? isVariantsValid : !!(bPlusSequence?.length > 0 && aPlusSequence?.length > 0); break;
            case 5: valid = !!(bPlusSequence?.length > 0 && aPlusSequence?.length > 0); break;
            case 6: valid = !!(bPlusSequence?.length > 0 && aPlusSequence?.length > 0); break;
            default: break;
        }
        return { valid, invalidFields: invalid };
    },
    buildSkuEntryExtra: (skuItem) => ({
        parentChildAttributes: skuItem?.parentChildAttributes || []
    }),
};

// ── Component ───────────────────────────────────────────────────────────────
const BulkFormHelper = ({ parsedData, zipFile, onBack, isNewMode = false, draftMeta = null, projectName = null }) => {
    const config = isNewMode ? newModeConfig : existingModeConfig;

    const {
        skuList, selectedSku, selectSku,
        parentSkus, childSkus, expandedParentSku, setExpandedParentSku,
        getSkuThumbnail, isLoadingImages,
        listingMode, formStep, setFormStep, showVariantsTab,
        isAsinDataFetched, isFormStepValid, getTotalFormSteps,
        isThemesPanelOpen, isFontsPanelOpen,
        isFloatingDropdownOpen, setIsFloatingDropdownOpen,
        singleListingFormValue,
        isLoading, bulkError,
        showQueuePopup, setShowQueuePopup,
        // Draft
        saveBulkDraft, isSavingDraft, draftSaveStatus,
    } = useBulkFormState(config, { parsedData, zipFile, draftMeta, projectName });

    return (
        <>
            {/* Floating SKU Block – rendered outside pageWrapper to avoid transform stacking context */}
            {!showQueuePopup && (isThemesPanelOpen || isFontsPanelOpen) && skuList.length > 0 && (
                <FloatingSkuDropdown
                    skuList={skuList}
                    selectedSku={selectedSku}
                    selectSku={selectSku}
                    getSkuThumbnail={getSkuThumbnail}
                    isOpen={isFloatingDropdownOpen}
                    setIsOpen={setIsFloatingDropdownOpen}
                />
            )}

            <div className={styles.pageWrapper}>
                {/* Processing overlay with Buddha logo */}
                {isLoading && <LoadingOverlay />}
                {/* Left Sidebar – hidden when theme/fonts panel is open or queue popup is shown */}
                {!showQueuePopup && !(isThemesPanelOpen || isFontsPanelOpen) && (
                    <SkuSidebar
                        skuList={skuList}
                        selectedSku={selectedSku}
                        selectSku={selectSku}
                        setFormStep={setFormStep}
                        parentSkus={parentSkus}
                        childSkus={childSkus}
                        expandedParentSku={expandedParentSku}
                        setExpandedParentSku={setExpandedParentSku}
                        getSkuThumbnail={getSkuThumbnail}
                        isLoadingImages={isLoadingImages}
                        onBack={onBack}
                    />
                )}

                {/* Right Side – Form (hidden when queue popup is shown) */}
                {!showQueuePopup && (
                <div className={styles.formSection}>
                    <div className={`${formStyles.formCard} ${(isThemesPanelOpen || isFontsPanelOpen) ? formStyles.formCardWithPanel : ''}`}>
                        {bulkError && !isLoading && <p className={formStyles.errorText}>{bulkError}</p>}
                        <div className={styles.draftBar}>
                            <button
                                type="button"
                                className={styles.saveDraftBtn}
                                onClick={() => saveBulkDraft(false)}
                                disabled={isSavingDraft}
                            >
                                {isSavingDraft ? 'Saving...'
                                    : draftSaveStatus === 'saved' ? 'Saved'
                                    : draftSaveStatus === 'auto_saved' ? 'Auto Saved'
                                    : 'Save Draft'}
                            </button>
                            {draftSaveStatus === 'error' && <span className={styles.draftStatusError}>Failed to save</span>}
                        </div>
                        <StepNavigationTabs
                            listingMode={listingMode}
                            formStep={formStep}
                            setFormStep={setFormStep}
                            isAsinDataFetched={isAsinDataFetched}
                            isFormStepValid={isFormStepValid}
                            isThemesPanelOpen={isThemesPanelOpen}
                            isFontsPanelOpen={isFontsPanelOpen}
                            showVariantsTab={showVariantsTab}
                            getTotalFormSteps={getTotalFormSteps}
                            hideAsinLookupTab={!isNewMode}
                        />
                        <SingleListingFormProvider value={singleListingFormValue}>
                            <FormSteps />
                        </SingleListingFormProvider>
                    </div>
                </div>
                )}

                {/* Queue notification popup – "Your task has been queued". On close, reset form by going back to upload. */}
                <QueueNotificationPopup
                    show={showQueuePopup}
                    onClose={() => setShowQueuePopup(false)}
                    onResetFiles={() => {
                        setShowQueuePopup(false);
                        onBack();
                    }}
                    title="Generation Started"
                />
            </div>
        </>
    );
};

export default BulkFormHelper;
